import type { EmailDetail } from '@webapp/account/src/resources/mailing/email-detail';
import type { MailFilters } from '@webapp/account/src/resources/mailing/filters';
import type {
    Mail,
    MailEditMode,
    MailGroup,
    MailParams,
    MailStat,
} from '@webapp/account/src/resources/mailing/mailing';
import { MailStatus, MailType, RecipientType } from '@webapp/account/src/resources/mailing/mailing';
import type { Recipient } from '@webapp/account/src/resources/mailing/recipient';
import type { Stat } from '@webapp/account/src/resources/mailing/stat';
import {
    selectLicense,
    selectLicenseInfo,
    selectSurveyQuotasById,
} from '@webapp/account/src/resources/profile/selectors';

import type { AlignSetting } from '@webapp/common/lib/ui';

import type { States } from '../states';
import { selectCurrentSurveyId } from '../survey/selectors';

import type { MailingState } from './state';

export const mailing = ({ Mailing }: States): MailingState => Mailing;

export const selectAllMailGroups = (states: States): Array<MailGroup> => {
    const m = mailing(states);

    return m.mailGroups;
};

export const selectVisibleMailGroups = (states: States): Array<MailGroup> => {
    const m = selectAllMailGroups(states);

    return m.filter(({ hidden }) => !hidden);
};

export const selectAllMailing = (states: States): Array<Mail> => {
    const m = mailing(states);

    return m.items.filter(({ status }) => status !== MailStatus.DELETED);
};

export const selectMailListFetch = (states: States): boolean => {
    const m = mailing(states);

    return m.fetch;
};

export const selectQuotasFetch = (states: States): boolean => {
    const m = mailing(states);

    return m.quotasFetch;
};

export const selectTodayEmailCount = (states: States): number => {
    const { isSingle } = selectLicenseInfo(states);
    const surveyId = selectCurrentSurveyId(states);
    const oneTimeLicense = selectSurveyQuotasById(surveyId)(states);
    const m = mailing(states);

    return isSingle ? oneTimeLicense?.emailLeft || 0 : m.todayEmailCount;
};

export const selectEmailDetailsTotalCount = (states: States): number => {
    const m = mailing(states);

    return m.emailDetailsTotalCount;
};

export const selectEmailDetails = (states: States): Array<EmailDetail> => {
    const m = mailing(states);

    return m.emailDetails;
};

export const selectEmailDetailsPage = (states: States): number => {
    const m = mailing(states);

    return m.emailDetailsPage;
};

export const selectEmailFilters = (states: States): MailFilters => {
    const m = mailing(states);

    return m.filters;
};

export const selectSendDates = (states: States): Array<string> => {
    const m = mailing(states);

    return m.sendDates;
};

export const selectMailingStat = (states: States): Stat => {
    const m = mailing(states);

    return m.stat;
};

export const selectCurrentMail = (states: States): Mail => {
    const m = mailing(states);

    return m.item;
};

export const selectCurrentMailId = (states: States): number => {
    const m = selectCurrentMail(states);

    return m.id;
};

export const selectCurrentMailStat = (states: States): MailStat => {
    const m = selectCurrentMail(states);

    return m.stat;
};

export const selectRecipients = (states: States): Array<Recipient> => {
    const m = selectCurrentMail(states);

    return m.recipients;
};

export const selectTodayExhausted = (states: States): boolean => {
    const todayEmailCount = selectTodayEmailCount(states);
    const recipientsCount = selectRecipientsCount(states);

    return recipientsCount > todayEmailCount;
};

export const selectSingleExhaustedById =
    (surveyId: number) =>
    (states: States): boolean => {
        const { isSingle } = selectLicenseInfo(states);
        const { surveys } = selectLicense(states);
        const survey = surveys.find(({ id }) => id === surveyId);
        const emailLeft = survey?.emailLeft || 0;
        const recipientsCount = selectRecipientsCount(states);

        return isSingle && recipientsCount > emailLeft;
    };

export const selectMailParams = (states: States): MailParams => {
    const m = selectCurrentMail(states);

    return m.params;
};

export const logoImage = (states: States): string => {
    const p = selectMailParams(states);

    return p.logoImage;
};

export const logoImageName = (states: States): string => {
    const p = selectMailParams(states);

    return p.logoImageName;
};

export const selectBody = (states: States): string => {
    const p = selectMailParams(states);

    return p.body;
};

export const buttonColor = (states: States): string => {
    const p = selectMailParams(states);

    return p.buttonColor;
};

export const buttonText = (states: States): string => {
    const p = selectMailParams(states);

    return p.buttonText;
};

export const selectHeaderColor = (states: States): string => {
    const p = selectMailParams(states);

    return p.headerColor;
};

export const selectFullHtml = (states: States): string => {
    const p = selectMailParams(states);

    return p.fullHtml;
};

export const logoPosition = (states: States): AlignSetting => {
    const p = selectMailParams(states);

    return p.logoPos;
};

export const selectEditMode = (states: States): MailEditMode => {
    const p = selectMailParams(states);

    return p.editMode;
};

export const selectSubject = (states: States): string => {
    const p = selectCurrentMail(states);

    return p.title;
};

export const selectMailGroups = (states: States): Array<MailGroup> => {
    const m = selectCurrentMail(states);

    return m.mailGroups;
};

export const selectHasRecipients = (states: States): boolean => {
    const g = selectMailGroups(states);
    const r = selectRecipients(states);

    return g.length + r.length > 0;
};

export const selectRecipientsCount = (states: States): number => {
    const g = selectMailGroups(states);
    const r = selectRecipients(states);

    return g.reduce((acc, data) => acc + (data?.emails.length || 0), 0) + r.length;
};

export const selectFetchGroup = (states: States): boolean => {
    const m = selectCurrentMail(states);

    return m.fetchGroup;
};

export const selectFetchUpload = (states: States): boolean => {
    const m = mailing(states);

    return m.fileUpload;
};

export const selectRecipientType = (states: States): RecipientType => {
    const m = selectCurrentMail(states);

    return m.who;
};

export const selectRecipientCount = (states: States): number => {
    const w = selectRecipientType(states);
    const s = selectCurrentMailStat(states);

    switch (w) {
        case RecipientType.NO_REPLY:
            return s.notAnswered;
        case RecipientType.REPLY:
            return s.answered;
        case RecipientType.ALL:
            return s.all;
    }

    return 0;
};

export const selectCanScheduleById =
    (surveyId: number) =>
    (states: States): boolean => {
        const hasRecipients = selectHasRecipients(states);
        const singleExhausted = selectSingleExhaustedById(surveyId)(states);
        const { mailType } = selectCurrentMail(states);
        const count = selectRecipientCount(states);

        return (hasRecipients && !singleExhausted) || (mailType === MailType.NOTIFY && count > 0);
    };

export const selectCanSend = (states: States): boolean => {
    const emailsLeft = selectTodayEmailCount(states);
    return emailsLeft > 0;
};
